import { getAuth } from "firebase/auth";
import { getApiUrl } from "./config";

/**
 * GET /api/tenants/:id/ledger - entries: [{ date, description, amount, status, balance }]
 * Auth: Bearer token (tenant or landlord). Use authToken if provided (e.g. magic link ?token=).
 */
export async function getTenantLedger(tenantId, authToken = null) {
  let token = authToken;
  if (!token) {
    const user = getAuth().currentUser;
    token = user ? await user.getIdToken() : null;
  }
  const res = await fetch(
    `${getApiUrl()}/tenants/${encodeURIComponent(tenantId)}/ledger`,
    {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load ledger (${res.status})`);
  }
  return res.json();
}

/**
 * GET /api/tenants/:id/signed-lease - returns { url, auditLogUrl? } for signed lease PDF.
 * Auth: Bearer token (tenant or landlord). Use authToken if provided (e.g. from URL ?token=).
 */
export async function getSignedLeaseUrl(tenantId, authToken = null) {
  let token = authToken;
  if (!token) {
    const user = getAuth().currentUser;
    token = user ? await user.getIdToken() : null;
  }
  const res = await fetch(
    `${getApiUrl()}/tenants/${encodeURIComponent(tenantId)}/signed-lease`,
    {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    }
  );
  if (res.status === 404) return { url: null, auditLogUrl: null };
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to get signed lease (${res.status})`);
  }
  return res.json();
}

/**
 * GET /api/tenants/:id/tickets - repair tickets for Maintenance tab.
 */
export async function getTenantTickets(tenantId, authToken = null) {
  let token = authToken;
  if (!token) {
    const user = getAuth().currentUser;
    token = user ? await user.getIdToken() : null;
  }
  const res = await fetch(
    `${getApiUrl()}/tenants/${encodeURIComponent(tenantId)}/tickets`,
    {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load tickets (${res.status})`);
  }
  return res.json();
}

/**
 * POST /api/tenants/:id/confirm-utilities - notify landlord that tenant confirmed utilities transferred.
 */
export async function confirmUtilities(tenantId, authToken = null) {
  let token = authToken;
  if (!token) {
    const user = getAuth().currentUser;
    token = user ? await user.getIdToken() : null;
  }
  const res = await fetch(
    `${getApiUrl()}/tenants/${encodeURIComponent(tenantId)}/confirm-utilities`,
    {
      method: "POST",
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to confirm (${res.status})`);
  }
  return res.json();
}

/**
 * PATCH /api/tenants/:id - e.g. { autopayEnabled: true }
 * Auth: Bearer token (tenant or landlord).
 */
/**
 * POST /api/tenants/:id/move-out: landlord only. Archives chat, marks tenant moved out, clears door activeTenantId, invalidates magic-link sessions.
 */
export async function postTenantMoveOut(tenantId, authToken = null) {
  let token = authToken;
  if (!token) {
    const user = getAuth().currentUser;
    token = user ? await user.getIdToken() : null;
  }
  if (!token) throw new Error("Not authenticated");
  const res = await fetch(
    `${getApiUrl()}/tenants/${encodeURIComponent(tenantId)}/move-out`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Move-out failed (${res.status})`);
  }
  return res.json();
}

export async function updateTenant(tenantId, payload, authToken = null) {
  let token = authToken;
  if (!token) {
    const user = getAuth().currentUser;
    token = user ? await user.getIdToken() : null;
  }
  const res = await fetch(
    `${getApiUrl()}/tenants/${encodeURIComponent(tenantId)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update (${res.status})`);
  }
  return res.json();
}
